<script setup lang="ts">
import SectionTitle from '../SectionTitle.vue';
import PlannerSeminarAccordion from './PlannerSeminarAccordion.vue';
import SeminarCard from '~/components/organisms/SeminarCard/SeminarCardV2.vue';
import { type FragmentType, graphql } from '~/gql';
import type { PlannerSeminarSectionFragment as PlannerSeminarSectionFragmentType } from '~/gql/graphql';

const props = defineProps<{
  planner: FragmentType<typeof PlannerSeminarSectionFragment>;
}>();

const PlannerSeminarSectionFragment = graphql(`
  fragment PlannerSeminarSection on Planner {
    seminars {
      id
      timerexUrl: timerexURL
      schedules {
        id
        endedAt
      }
      ...SeminarCardV2Seminar
    }
  }
`);

const seminars = computed(
  () => getFragment(PlannerSeminarSectionFragment, props.planner).seminars
);
type SeminarType = PlannerSeminarSectionFragmentType['seminars'][number];

const isOpenSeminar = (seminar: SeminarType): boolean =>
  !!seminar.timerexUrl ||
  seminar.schedules.some(
    (schedule) => new Date(schedule.endedAt) >= new Date()
  );

const openSeminars = computed(() =>
  seminars.value.filter((seminar) => isOpenSeminar(seminar))
);

const closedSeminars = computed(() =>
  seminars.value.filter((seminar) => !isOpenSeminar(seminar))
);
</script>

<template>
  <section v-if="seminars?.length != null && seminars.length > 0">
    <SectionTitle title="マネーセミナー" />
    <div :class="$style.list">
      <div :class="$style.list__header">
        現在開催中のセミナー({{ openSeminars.length }}件)
      </div>
      <hr :class="$style.list__divider" />
      <div :class="$style.list__content">
        <SeminarCard
          v-for="seminar in openSeminars"
          :key="seminar.id"
          :seminar="seminar"
          :class="$style.seminar"
        />
      </div>
    </div>
    <div v-if="closedSeminars.length">
      <PlannerSeminarAccordion :class="$style.list">
        <template #title>
          過去開催されたセミナー({{ closedSeminars.length }}件)
        </template>
        <SeminarCard
          v-for="seminar in closedSeminars"
          :key="seminar.id"
          :seminar="seminar"
          :class="$style.seminar"
        />
      </PlannerSeminarAccordion>
    </div>
  </section>
</template>

<style lang="scss" module>
.list {
  margin-top: 24px;

  &__header {
    font-weight: bold;
  }

  &__divider {
    flex: 0 0 auto;
    margin: 8px 0 0;
    border-top: 1px solid $color-border-low-emphasis;
    border-left: none;
    border-right: none;
    border-bottom: none;

    &:nth-last-child(n + 2) {
      margin-bottom: 16px;
    }
  }
}

.seminar {
  &:nth-child(n + 2) {
    margin-top: 24px;
  }
}
</style>
